import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import {
  InfoList,
  SectionHeader,
} from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import { FEEDBACK_QUESTIONS, FEEDBACK_STATUSES, statusColor } from './feedbackConstants'
// MUI
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import LinkIcon from '@mui/icons-material/Link'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SendIcon from '@mui/icons-material/Send'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const Section = ({ title, caption, action, children }) => (
  <Stack spacing={1.25}>
    <SectionHeader title={title} caption={caption} action={action} />
    {children}
  </Stack>
)

const YesNo = ({ label, checked }) => (
  <Stack direction="row" alignItems="center" spacing={1} py={0.6}>
    {checked ? (
      <CheckCircleIcon fontSize="small" color="success" />
    ) : (
      <CancelOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
    )}
    <Typography
      variant="body2"
      color={checked ? 'text.primary' : 'text.secondary'}
      fontWeight={checked ? 600 : 400}
    >
      {label}
    </Typography>
  </Stack>
)

const ToggleChip = ({ active, label, icon, color, onClick }) => (
  <Chip
    icon={icon}
    label={label}
    clickable
    onClick={onClick}
    color={active ? color : 'default'}
    variant={active ? 'filled' : 'outlined'}
    sx={{ borderRadius: 999, fontWeight: 600 }}
  />
)

const FeedbackDrawer = ({
  selectedFeedback,
  setSelectedFeedback,
  loadFeedback,
  isIncidentView,
}) => {
  const [changesPresent, setChangesPresent] = useState(false)
  const [statusAnchorEl, setStatusAnchorEl] = useState(null)
  const [snack, setSnack] = useState(null) // { severity, message }
  const [newComment, setNewComment] = useState('')
  const [postingComment, setPostingComment] = useState(false)
  const { user } = useContext(UserContext)
  const navigate = useNavigate()

  // Reset local state whenever a different item is opened
  useEffect(() => {
    setChangesPresent(false)
    setNewComment('')
  }, [selectedFeedback?._id])

  const patch = (changes) => {
    setChangesPresent(true)
    setSelectedFeedback((prev) => ({ ...prev, ...changes }))
  }

  const handleUpdate = () => {
    API.put(`/patient-feedback/${selectedFeedback._id}`, selectedFeedback)
      .then((res) => {
        if (res.status === 200) {
          setSnack({ severity: 'success', message: 'Feedback updated' })
          loadFeedback?.()
          setChangesPresent(false)
        } else {
          setSnack({ severity: 'error', message: 'Unable to update feedback' })
        }
      })
      .catch(() =>
        setSnack({ severity: 'error', message: 'Unable to update feedback' }),
      )
  }

  const handleClose = () => {
    if (
      changesPresent &&
      !window.confirm('Discard unsaved changes to this feedback?')
    ) {
      return
    }
    setSelectedFeedback(null)
  }

  const handleCreateNewIncidentFromFeedback = () => {
    if (!selectedFeedback) return
    API.post('/incident', {
      linkedFeedback: selectedFeedback._id,
      incidentDate: selectedFeedback.createdAt,
      incidentType: 'Patient Feedback',
      description:
        'Patient Complaint, see linked feedback for further information',
    }).then((res) => {
      if (res.status === 200) {
        handleUpdate()
        navigate(`/incidents/${res.data}`)
      }
    })
  }

  const handleAddComment = () => {
    const comment = newComment.trim()
    if (!comment || !selectedFeedback) return
    setPostingComment(true)
    API.post(`/patient-feedback/${selectedFeedback._id}/comments`, { comment })
      .then((res) => {
        if (res.status === 200) {
          setSelectedFeedback((prev) => ({
            ...prev,
            comments: [
              ...(prev.comments ?? []),
              {
                _id: `local-${Date.now()}`,
                author: {
                  _id: user?._id,
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  photograph: user?.photograph,
                },
                comment,
                createdAt: new Date().toISOString(),
              },
            ],
          }))
          setNewComment('')
          loadFeedback?.()
        } else {
          setSnack({ severity: 'error', message: 'Unable to add comment' })
        }
      })
      .catch(() => setSnack({ severity: 'error', message: 'Unable to add comment' }))
      .finally(() => setPostingComment(false))
  }

  const handleDeleteComment = (commentId) => {
    API.delete(`/patient-feedback/${selectedFeedback._id}/comments/${commentId}`)
      .then((res) => {
        if (res.status === 200) {
          setSelectedFeedback((prev) => ({
            ...prev,
            comments: (prev.comments ?? []).filter((c) => c._id !== commentId),
          }))
          loadFeedback?.()
        }
      })
      .catch(() => setSnack({ severity: 'error', message: 'Unable to delete comment' }))
  }

  const f = selectedFeedback
  const hasContact = f?.firstName || f?.lastName || f?.email || f?.phone
  const crewNames = (f?.staff ?? [])
    .map((s) =>
      [s.userAccount?.firstName, s.userAccount?.lastName]
        .filter(Boolean)
        .join(' '),
    )
    .filter(Boolean)
  const comments = [...(f?.comments ?? [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  )

  return (
    <Drawer
      open={!!f}
      anchor="right"
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 500, lg: 580 },
          maxWidth: '100vw',
          bgcolor: 'background.default',
        },
      }}
    >
      {f && (
        <Stack height="100%">
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(38, 142, 187, 0.12)',
                  color: 'anMainBlue.main',
                  flexShrink: 0,
                }}
              >
                <ForumOutlinedIcon />
              </Box>
              <Box minWidth={0} flex={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                    Patient Feedback
                  </Typography>
                  <Chip
                    size="small"
                    label={f.status ?? 'New'}
                    color={statusColor(f.status)}
                    onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                  <Menu
                    anchorEl={statusAnchorEl}
                    open={!!statusAnchorEl}
                    onClose={() => setStatusAnchorEl(null)}
                  >
                    {FEEDBACK_STATUSES.map((s) => (
                      <MenuItem
                        key={s}
                        selected={(f.status ?? 'New') === s}
                        onClick={() => {
                          patch({ status: s })
                          setStatusAnchorEl(null)
                        }}
                      >
                        {s}
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Received {moment(f.createdAt).format('Do MMM YYYY [at] HH:mm')}
                  {f.relationship && ` · from a ${f.relationship.toLowerCase()}`}
                  {f.contactType && ` · ${f.contactType}`}
                </Typography>
              </Box>
              <IconButton onClick={handleClose} size="small" aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Body */}
          <Box flex={1} overflow="auto" p={2}>
            <Stack spacing={3}>
              <Section
                title="Triage"
                caption="Mark the feedback so it feeds the right KPIs and CQC evidence"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <ToggleChip
                    active={!!f.isComplement}
                    label="Compliment"
                    icon={<ThumbUpIcon />}
                    color="success"
                    onClick={() => patch({ isComplement: !f.isComplement })}
                  />
                  <ToggleChip
                    active={!!f.isComplaint}
                    label="Complaint"
                    icon={<ThumbDownIcon />}
                    color="error"
                    onClick={() => patch({ isComplaint: !f.isComplaint })}
                  />
                </Stack>
                {!isIncidentView && f.incident && (
                  <Button
                    onClick={() => navigate(`/incidents/${f.incident._id}`)}
                    variant="outlined"
                    startIcon={<ReportProblemOutlinedIcon />}
                    sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none' }}
                  >
                    Open linked incident
                  </Button>
                )}
                {!isIncidentView && f.isComplaint && !f.incident && (
                  <Alert
                    severity="warning"
                    icon={<ReportProblemOutlinedIcon />}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleCreateNewIncidentFromFeedback}
                      >
                        Create incident
                      </Button>
                    }
                  >
                    Complaints are best investigated as a governance incident.
                  </Alert>
                )}
              </Section>

              <Section title="Care record">
                {f.epcr ? (
                  <>
                    <InfoList
                      rows={[
                        {
                          label: 'ePCR',
                          value: (
                            <Chip
                              size="small"
                              icon={<LinkIcon />}
                              label={f.epcr.epcrNumber ?? 'Linked'}
                              color="anBlue"
                              sx={{ borderRadius: 1, fontWeight: 700 }}
                            />
                          ),
                        },
                        {
                          label: 'Incident date',
                          value: f.epcr.incident?.incidentDate
                            ? moment(f.epcr.incident.incidentDate).format('Do MMM YYYY')
                            : null,
                        },
                        {
                          label: 'Crew',
                          value: crewNames.length ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                              justifyContent="flex-end"
                            >
                              {crewNames.map((n) => (
                                <Chip key={n} size="small" label={n} sx={{ borderRadius: 1 }} />
                              ))}
                            </Stack>
                          ) : null,
                        },
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Linked by the patient after confirming surname and date of
                      birth.
                    </Typography>
                    <Button
                      onClick={() => navigate(`/patient-report-forms/${f.epcr._id}`)}
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none' }}
                    >
                      View care record
                    </Button>
                  </>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: (t) => `1px dashed ${t.palette.divider}`,
                      color: 'text.secondary',
                    }}
                  >
                    <LockOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                      Left anonymously — not linked to a care record.
                    </Typography>
                  </Stack>
                )}
              </Section>

              <Section title="What they told us">
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    <FormatQuoteIcon sx={{ color: 'text.disabled', transform: 'scaleX(-1)' }} />
                    <Typography
                      variant="body1"
                      color={f.feedbackComments ? 'text.primary' : 'text.disabled'}
                      sx={{ whiteSpace: 'pre-wrap', fontStyle: f.feedbackComments ? 'normal' : 'italic' }}
                    >
                      {f.feedbackComments || 'No written comments were left.'}
                    </Typography>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Would recommend us
                  </Typography>
                  <Chip
                    size="small"
                    label={f.recommend || 'Not answered'}
                    color={
                      f.recommend === 'Yes'
                        ? 'success'
                        : f.recommend === 'No'
                          ? 'error'
                          : 'default'
                    }
                    sx={{ borderRadius: 1, fontWeight: 600 }}
                  />
                </Stack>
                <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
                  {FEEDBACK_QUESTIONS.map((q) => (
                    <YesNo key={q.key} label={q.label} checked={!!f[q.key]} />
                  ))}
                </Stack>
              </Section>

              <Section title="Contact">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={f.mayContact ? <CheckCircleIcon /> : <CancelOutlinedIcon />}
                    color={f.mayContact ? 'success' : 'default'}
                    label={f.mayContact ? 'Happy to be contacted' : 'Do not contact'}
                    sx={{ borderRadius: 1 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={f.mayShare ? <CheckCircleIcon /> : <CancelOutlinedIcon />}
                    color={f.mayShare ? 'success' : 'default'}
                    label={f.mayShare ? 'Feedback may be shared' : 'Do not share'}
                    sx={{ borderRadius: 1 }}
                  />
                </Stack>
                {hasContact ? (
                  <InfoList
                    rows={[
                      {
                        label: 'Name',
                        value: [f.firstName, f.lastName].filter(Boolean).join(' '),
                      },
                      {
                        label: 'Email',
                        value: f.email ? (
                          <Typography
                            component="a"
                            href={`mailto:${f.email}`}
                            variant="body2"
                            fontWeight={600}
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {f.email}
                          </Typography>
                        ) : null,
                      },
                      {
                        label: 'Phone',
                        value: f.phone ? (
                          <Typography
                            component="a"
                            href={`tel:${f.phone}`}
                            variant="body2"
                            fontWeight={600}
                          >
                            {f.phone}
                          </Typography>
                        ) : null,
                      },
                    ]}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No contact details were left.
                  </Typography>
                )}
              </Section>

              <Section
                title="Learning"
                caption="What changed or was shared as a result — used as CQC evidence"
              >
                <TextField
                  value={f.learning ?? ''}
                  onChange={(e) => patch({ learning: e.target.value })}
                  placeholder="e.g. Shared with the crew at the team meeting; heat-welfare plan updated"
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Section>

              <Section
                title="Discussion"
                caption={`${comments.length} comment${comments.length === 1 ? '' : 's'}`}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleAddComment()
                      }
                    }}
                    placeholder="Add a comment for the governance team"
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                  />
                  <Tooltip title="Post comment">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleAddComment}
                        disabled={postingComment || !newComment.trim()}
                      >
                        <SendIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No comments yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {comments.map((c) => (
                      <Stack
                        key={c._id}
                        direction="row"
                        spacing={1.25}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Avatar
                          src={c.author?.photograph}
                          sx={{ width: 32, height: 32, borderRadius: 1.5 }}
                        >
                          {c.author?.firstName?.[0]}
                        </Avatar>
                        <Box minWidth={0} flex={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {c.author
                                ? `${c.author.firstName ?? ''} ${c.author.lastName ?? ''}`.trim()
                                : 'Unknown user'}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                              <Typography variant="caption" color="text.secondary">
                                {moment(c.createdAt).format('Do MMM YYYY · HH:mm')}
                              </Typography>
                              {!String(c._id).startsWith('local-') && (
                                <IconButton
                                  size="small"
                                  aria-label="Delete comment"
                                  onClick={() => handleDeleteComment(c._id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              )}
                            </Stack>
                          </Stack>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {c.comment}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Section>
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'background.paper',
              borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              {changesPresent && (
                <Typography variant="caption" color="warning.main" sx={{ mr: 'auto' }}>
                  Unsaved changes
                </Typography>
              )}
              <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={!changesPresent}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Save changes
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}
      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
      >
        <Alert severity={snack?.severity ?? 'success'} onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Drawer>
  )
}

export default FeedbackDrawer
